import { t } from 'i18next';
import { formatNumberWithDecimalPlaces, scaleCalculator } from 'utils';
import { ChartData } from 'queries/features/contract';
import { dayjs, Dayjs } from 'lib/dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { LineSeriesOption } from 'echarts';
import { toPng } from 'html-to-image';
import {
  mountAlarmClosedAt,
  mountAlarmMarkers,
  mountDataLine,
  mountRiskArea,
  mountThresholdLine,
} from './elementsParse';
import { getSavedFilters } from '../hooks/useFiltersLocalStorage';
import { colorsPalette } from '@shape/lighthouse-components';

dayjs.extend(isSameOrAfter);
export const abreviateUnits = (unitName?: string) =>
  t(`formats:units.${unitName}`, { defaultValue: unitName ?? '' });

export const valueFormatter = (value: number | null, unit: MountDataLineProps['unit']) => {
  const { getScaledValue } = scaleCalculator(unit.values, 10);
  return (
    (typeof value === 'number' &&
      `${formatNumberWithDecimalPlaces(getScaledValue(value), 2)} ${abreviateUnits(unit.name)}`) ||
    '-'
  );
};

export const buildTagsConfig = (tags: ChartTag[]) => {
  const sensorInfo = new Map<string, any>();
  tags.forEach(
    ({ reference, unit_of_measurement, name, category, description, tooltipText }, index) => {
      if (sensorInfo.has(reference)) {
        const sensor = sensorInfo.get(reference);
        sensorInfo.set(reference, {
          ...sensor,
          category: category ? [...sensor.category, ...category] : sensor.category,
        });
      } else {
        sensorInfo.set(reference, {
          label: name ?? reference,
          category,
          unit_of_measurement,
          color:
            index < colorsPalette.length
              ? colorsPalette[index]
              : colorsPalette[index - colorsPalette.length],
          description: description ?? reference,
          reference,
        });
      }
    },
  );
  return sensorInfo;
};
export const formatChartData = (
  chartData: ChartData[],
  tagsConfig: Map<string, any>,
): { sensors: GraphicSensor[]; thresholds: GraphicThreshold[]; units: GraphicUnit[] } => {
  const result = chartData.reduce(
    (
      acc,
      { tag, values, unit = 'Risk', threshold, description },
      index,
    ): { sensors: GraphicSensor[]; thresholds: GraphicThreshold[]; units: GraphicUnit[] } => {
      const defaultTag = {
        label: tag,
        unit_of_measurement: unit,
        color: colorsPalette[index],
        description,
      };

      const sensor = tagsConfig.get(tag) ?? defaultTag;
      const shouldTransform = values?.every(
        (value) => value.value <= 0.01 && value.value >= -0.05 && value.value !== 0,
      );

      if (shouldTransform) {
        values.forEach((value) => {
          value.value *= 100;
        });
        if (unit && !unit.includes('10^-2')) {
          unit = `${unit} 10^-2`;
        }
      }
      // unit

      const unitIndex = acc.units.findIndex(({ name }) => name === unit);

      if (unitIndex !== -1) {
        acc.units[unitIndex].values = [
          ...acc.units[unitIndex].values,
          ...values.map(({ value }) => value),
        ].filter((item, index, arr) => item != null && arr.indexOf(item) === index);
      } else {
        acc.units.push({
          name: unit,
          values: [...values.map(({ value }) => value)].filter(
            (item, index, arr) => item != null && arr.indexOf(item) === index,
          ),
        });
      }
      // sensor
      acc.sensors.push({
        label: sensor.label,
        unit,
        color: sensor.color,
        data: values.map(({ timestamp, value }) => {
          return {
            x: new Date(timestamp),
            y: value,
          };
        }),
      });

      // thereshold
      if (threshold) {
        acc.thresholds.push({
          parent: tag,
          unit: unit || '',
          color: '#E43030',
          data: [
            {
              name: tag,
              values: threshold?.map((item) => ({
                x: new Date(item.timestamp),
                y: item.output_threshold,
              })),
            },
          ],
        });
      }
      return acc;
    },
    { sensors: [], thresholds: [], units: [] } as {
      sensors: GraphicSensor[];
      thresholds: GraphicThreshold[];
      units: GraphicUnit[];
    },
  );
  return result;
};
export const buildSensorLineSeries = ({
  sensors,
  units,
  isConnectNulls,
}: {
  sensors: GraphicSensor[];
  units: GraphicUnit[];
  isConnectNulls?: boolean;
}) => {
  let _startDate: Date | undefined;
  let _endDate: Date | undefined;
  return sensors.reduce((acc, sensor) => {
    const sensorUnit = units.find(({ name }) => name === sensor.unit);
    if (sensorUnit && sensor.data.length) {
      const [firstDate, lastDate] = [sensor.data[0].x, sensor.data[sensor.data.length - 1].x];

      if (!_startDate || dayjs(firstDate).isBefore(_startDate)) _startDate = firstDate;
      if (!_endDate || dayjs(lastDate).isAfter(_endDate)) _endDate = lastDate;

      return [
        ...acc,
        mountDataLine({
          isConnectNulls,
          name: sensor.label,
          color: sensor.color,
          data: sensor.data,
          start: _startDate,
          end: _endDate,
          unit: sensorUnit,
        }),
      ];
    }
    return acc;
  }, [] as unknown[]);
};
export const buildThresholds = (
  thresholds: GraphicThreshold[],
  units: GraphicUnit[],
  dateRange: {
    start: Dayjs;
    end: Dayjs;
  },
  customStyle?: Partial<MountThresholdLineProps>,
) => {
  const lineSeries: unknown[] = [];

  if (dateRange.start && dateRange.end) {
    thresholds?.forEach(({ data, unit, color, parent }) => {
      const unitValues = units.find(({ name }) => name === unit)?.values || [];
      const { getValueScale } = scaleCalculator(unitValues, 10);

      data.forEach((threshold, index, thresholdArray) => {
        const result: [Date, number | null][] = [];

        const allNormalValues = threshold.values.every(({ y }) => y === 0.75);

        threshold.values.forEach(({ x, y }, _, arr) => {
          let normalizedValue = typeof y === 'number' ? getValueScale(y) : null;

          if (allNormalValues) {
            normalizedValue = y as number;
          }

          if (normalizedValue !== null) {
            if (arr.length === 1) {
              result.push([dateRange.start.toDate(), normalizedValue]);
              result.push([dateRange.end.toDate(), normalizedValue]);
            } else result.push([x, normalizedValue]);
          }
        });

        lineSeries.push(
          mountThresholdLine({
            name: `${parent}-${threshold.name}`,
            color,
            data: result,
            style: index === 0 || index === thresholdArray.length - 1 ? 'dotted' : 'dashed',
            ...customStyle,
          }),
        );
      });
    });
  }
  return lineSeries;
};

export const buildSeries = ({
  sensors,
  units,
  alarmsInfo,
  riskArea,
  dateRange,
  thresholds,
  connectNulls,
  customThrehold,
  chartId,
}: BuildSeriesProps) => {
  const lineSeries = buildSensorLineSeries({ sensors, units, isConnectNulls: connectNulls });

  const alarmMarkers =
    alarmsInfo?.alarmsData?.map((alarm, index) => {
      const { start, end, color } = alarm;
      const isZoomActive = !!getSavedFilters(chartId).zoomInterval;
      return mountAlarmMarkers({
        alarm: [
          dayjs.utc(start).local().toDate(),
          end ? dayjs.utc(end).local().toDate() : dayjs().toDate(),
        ],
        dateRange,
        color,
        index,
        isZoomActive,
      });
    }) ?? [];

  const closedAtMarkers =
    alarmsInfo?.closedAtAlarms?.map((closedDate, index) => {
      return mountAlarmClosedAt({ alarm: dayjs.utc(closedDate).local().toDate(), index });
    }) ?? [];

  const redArea = riskArea?.length ? [mountRiskArea({ data: riskArea })] : [];
  const theresholdLines = buildThresholds(thresholds ?? [], units, dateRange, customThrehold);
  return [
    ...lineSeries,
    ...alarmMarkers,
    ...closedAtMarkers,
    ...redArea,
    ...theresholdLines,
  ] as LineSeriesOption[];
};
export const downloadComponentAsImage = (
  componentRef?: React.RefObject<HTMLDivElement>,
  imageName?: string,
) => {
  if (!componentRef || !componentRef?.current) return;
  toPng(componentRef.current).then((dataUrl) => {
    const link = document.createElement('a');
    link.download = imageName ?? 'chart.png';
    link.href = dataUrl;
    link.click();
  });
};
